/**
 * Gathering surfaces — the handful of pieces the page, builder, sheets and manager share that
 * `components/club/clubUi` doesn't already have (design_handoff_gatherings README §Design tokens).
 * Everything else (PillButton, RingTab, Face, TagPill, Switch) is imported from clubUi so the
 * gathering screens read as the club pages do.
 */
import React, { useEffect } from 'react';
import { RING_ON } from '../surface/SurfaceStyleContext';
import { safePad } from '../../utils/safeArea';

export const CLASH_BG = '#FFF8EA';
export const CLASH_FG = '#B45309';
export const CLASH_BAR = '#F59E0B';
export const STARTER = '#FF00FF';
export const STARTER_TRACK = '#FBD5FB';
export const CURATED = '#B5B1FF';
export const CYAN_TINT = '#E6FFFF';
export const RED_TINT = '#FEE2E2';

export const kicker = (size = 9, tracking = '.16em') => ({ fontSize: size, fontWeight: 700, letterSpacing: tracking, textTransform: 'uppercase' });
export const pillInput = { height: 48, padding: '0 18px', border: 'none', borderRadius: 999, background: '#F5F5F5', fontFamily: 'inherit', fontSize: 14, color: '#000', outline: 'none', width: '100%', boxSizing: 'border-box' };
export const softTextarea = { minHeight: 88, resize: 'vertical', padding: '14px 18px', border: 'none', borderRadius: 20, background: '#F5F5F5', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.6, color: '#000', outline: 'none', width: '100%', boxSizing: 'border-box' };

/** `SAME TIME AS <TITLE>` / `OVERLAPS <TITLE>` — replaces every "⚠". */
export function OverlapChip({ title, verb = 'SAME TIME AS', size = 9, style }) {
  return (
    <span className="font-mono" style={{ alignSelf: 'flex-start', padding: '3px 9px', borderRadius: 999, background: CLASH_BG, color: CLASH_FG, fontSize: size, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', ...style }} data-testid="overlap-chip">
      {verb} {title}
    </span>
  );
}

/** A labelled field: 9px grey kicker over the control. */
export function Field({ label, children, style, max }) {
  return (
    <label className="flex flex-col font-mono" style={{ gap: 6, maxWidth: max, ...style }}>
      <span style={{ ...kicker(), color: '#6B7280' }}>{label}</span>
      {children}
    </label>
  );
}

/**
 * Two-option segmented card: a #F5F5F5 track (radius 22) with radius-18 options, each a label
 * plus a hint; the active one wears the ring. Without hints it is the compact pill track
 * (black active) used for the agenda view toggle and the RSVP group.
 */
export function Segmented({ options, value, onChange, label, max = 460, compact = false, height = 32, ring = true, testId, ariaLabel }) {
  const hinted = !compact && options.some((o) => o.hint);
  const track = compact
    ? { display: 'inline-flex', gap: 3, padding: 3, borderRadius: 999, background: '#F5F5F5' }
    : { display: 'grid', gridTemplateColumns: `repeat(${options.length},minmax(0,1fr))`, gap: 4, padding: 4, borderRadius: 22, background: '#F5F5F5', maxWidth: max };
  return (
    <div className="flex flex-col font-mono" style={{ gap: 8 }}>
      {label && <span style={{ ...kicker(), color: '#6B7280' }}>{label}</span>}
      <span role="radiogroup" aria-label={ariaLabel || label} style={track} data-testid={testId}>
        {options.map((o) => {
          const on = o.key === value;
          const base = compact
            ? { height, padding: '0 12px', borderRadius: 999, background: on ? '#000' : 'transparent', color: on ? '#fff' : '#000', border: 'none', fontSize: 9, fontWeight: 700, letterSpacing: '.12em', whiteSpace: 'nowrap' }
            : { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3, padding: hinted ? '10px 14px' : '12px 14px', border: '2px solid transparent', borderRadius: 18, background: on ? (ring ? RING_ON : '#000') : 'transparent', color: on ? '#fff' : '#000', textAlign: 'left' };
          return (
            <button key={o.key} type="button" role="radio" aria-checked={on} onClick={() => onChange(on && o.toggle ? null : o.key)} className="font-mono"
              style={{ fontFamily: 'inherit', cursor: 'pointer', textTransform: 'uppercase', transition: 'background-color .2s, color .2s', boxSizing: 'border-box', ...base }}>
              <span style={{ fontSize: compact ? 9 : 10, fontWeight: 700, letterSpacing: compact ? '.12em' : '.14em' }}>{o.label}</span>
              {o.hint && <span style={{ fontSize: 10, lineHeight: 1.4, opacity: 0.75, textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>{o.hint}</span>}
            </button>
          );
        })}
      </span>
    </div>
  );
}

/** 56 × 32 switch: track black on / #E5E7EB off, 26px white knob. */
export function BigSwitch({ on, onChange, label, disabled = false, testId }) {
  return (
    <button type="button" role="switch" aria-checked={!!on} aria-label={label} disabled={disabled} onClick={() => !disabled && onChange?.(!on)} data-testid={testId}
      style={{ flexShrink: 0, width: 56, height: 32, padding: 3, border: 'none', borderRadius: 999, background: on ? '#000' : '#E5E7EB', cursor: disabled ? 'default' : 'pointer', display: 'flex', justifyContent: on ? 'flex-end' : 'flex-start', transition: 'background-color .2s', boxSizing: 'border-box', opacity: disabled ? 0.5 : 1 }}>
      <span style={{ width: 26, height: 26, borderRadius: '50%', background: '#fff', display: 'block' }} />
    </button>
  );
}

/**
 * Sheet: a 520px white modal with square corners on desktop, a bottom sheet (24px top radius)
 * on the phone; scrim rgba(0,0,0,.55); max 88vh and scrolls; 36px round × button.
 */
export function Sheet({ open, onClose, title, phone = false, width = 520, children, testId = 'gathering-sheet' }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div onClick={onClose} className="fixed inset-0 flex justify-center" style={{ zIndex: 80, background: 'rgba(0,0,0,.55)', alignItems: phone ? 'flex-end' : 'center', padding: phone ? 0 : 24, boxSizing: 'border-box' }} data-testid={`${testId}-backdrop`}>
      <div role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()} className="flex flex-col bg-white font-mono" style={{ width: '100%', maxWidth: phone ? '100%' : width, maxHeight: '88vh', overflow: 'auto', borderRadius: phone ? '24px 24px 0 0' : 0, padding: phone ? safePad('18px 16px') : '22px 24px', boxSizing: 'border-box', gap: 16 }} data-testid={testId}>
        <span className="flex items-center justify-between" style={{ gap: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' }}>{title}</span>
          <button type="button" onClick={onClose} aria-label="Close" style={{ width: 36, height: 36, flexShrink: 0, border: 'none', borderRadius: '50%', background: '#F2F2F2', fontFamily: 'inherit', fontSize: 16, cursor: 'pointer' }}>×</button>
        </span>
        {children}
      </div>
    </div>
  );
}

/** The ring-pill CTA for money: 54px, white text. */
export function RingCta({ children, onClick, disabled = false, height = 54, type = 'button', style, ...rest }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled} className="font-mono"
      style={{ height, width: '100%', border: '2px solid transparent', borderRadius: 999, background: RING_ON, color: '#fff', fontFamily: 'inherit', fontSize: 12, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1, boxSizing: 'border-box', ...style }} {...rest}>
      {children}
    </button>
  );
}

/** Rows of key / value on 2px #F2F2F2 rules (READ IT BACK, the entry sheet). */
export function KVRows({ rows, testId }) {
  return (
    <div className="flex flex-col font-mono" data-testid={testId}>
      {rows.map(([k, v], i) => (
        <span key={k} className="flex flex-wrap items-baseline justify-between" style={{ gap: '4px 16px', padding: '11px 0', borderTop: i ? '2px solid #F2F2F2' : 'none', fontSize: 12 }}>
          <span style={{ ...kicker(), color: '#6B7280' }}>{k}</span>
          <span style={{ fontWeight: 700, textAlign: 'right' }}>{v}</span>
        </span>
      ))}
    </div>
  );
}

/** A thin progress bar: 6px, track + fill colours. */
export function Bar({ pct, track = '#F2F2F2', fill = '#000', height = 6, width = '100%', style }) {
  return (
    <span style={{ display: 'block', width, height, borderRadius: 999, background: track, overflow: 'hidden', ...style }} aria-hidden="true">
      <span style={{ display: 'block', height: '100%', width: `${Math.max(0, Math.min(100, pct || 0))}%`, background: fill }} />
    </span>
  );
}

/** Round face + name pill (#F5F5F5 by default). */
export function FacePill({ src, name, size = 28, bg = '#F5F5F5', right, onClick, testId }) {
  const Tag = onClick ? 'button' : 'span';
  return (
    <Tag type={onClick ? 'button' : undefined} onClick={onClick} className="font-mono" title={name} data-testid={testId}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: `4px ${right ? 6 : 12}px 4px 4px`, borderRadius: 999, background: bg, border: 'none', fontFamily: 'inherit', cursor: onClick ? 'pointer' : 'default', maxWidth: '100%' }}>
      <RoundFace src={src} name={name} size={size} />
      <span className="truncate" style={{ fontSize: 11, fontWeight: 700 }}>{name}</span>
      {right}
    </Tag>
  );
}

const TINTS = ['#A8D8EA', '#FFCCE6', '#C6B8FF', '#FAE1B4', '#B9F6CA'];
/** Round avatar, or a tinted initial. */
export function RoundFace({ src, name = '', size = 38, style }) {
  const tint = TINTS[String(name).length % TINTS.length];
  return (
    <span className="flex flex-none items-center justify-center overflow-hidden rounded-full font-mono" style={{ width: size, height: size, background: src ? '#E5E7EB' : tint, fontSize: Math.round(size * 0.4), fontWeight: 800, color: '#000', ...style }}>
      {src ? <img src={src} alt="" referrerPolicy="no-referrer" className="block h-full w-full object-cover" /> : (name || '?').trim().charAt(0).toUpperCase()}
    </span>
  );
}
